import {
    EmailMessagePayloadWithAttachments,
    NewCommentPayload,
    OfflineReportsConfiguration,
    OfflineReportsCreditsBalance,
    OrderManagementRequest,
    ShareReportPayload,
    SupportRequest,
    UserAccountDetails,
} from 'core/entities/offline-reports/offline-reports';
import { fetchWithBasePath, fetchWrapper } from 'API/authentification/fetch-wrapper';
import {
    AttachmentItem,
    Comment,
    OfflineReportDetails,
    OfflineReportListItem,
} from 'core/entities';
import type { PlacerResponseData } from '@placer-ui/types';
import { TableauTicketResponse } from 'API/external-iframe-reports-api/response';
import { userCookieStorage } from 'core/authentification';
import { getAppGatewayEndpoint } from 'core/default-endpoint-url';
import type {
    SubmitRequestFlowPayload,
    SubmitSupportRequestPayload,
    SubmitSupportRequestResponse,
} from './types';
import { getFeatureFlags } from 'core/flow-control';

const XTRA_ENDPOINT_ENDPOINT = `${getAppGatewayEndpoint()}/xtra-reports`;
const XTRA_GET_REPORTS_DATA_ENDPOINT = `${XTRA_ENDPOINT_ENDPOINT}/get-reports-data`;
const XTRA_UPDATE_REPORTS_DATA_ENDPOINT = `${XTRA_ENDPOINT_ENDPOINT}/update-reports-data`;
export const XTRA_CONTENT_VERSION_ENDPOINT = `${XTRA_ENDPOINT_ENDPOINT}/content-version`;

export const supportApi = {
    getOfflineReports(): Promise<OfflineReportListItem[]> {
        return fetchWrapper({ targetUrl: '/offline_reports/metadata/' });
    },
    getOfflineReportDetails(id: string): Promise<OfflineReportDetails> {
        return fetchWrapper({ targetUrl: `/offline_reports/metadata/${id}/` });
    },
    async submitRequest(
        payload: SubmitSupportRequestPayload,
    ): Promise<SubmitSupportRequestResponse> {
        const { dev_enable_submit_sf_tickets_ff } = getFeatureFlags();

        if (!dev_enable_submit_sf_tickets_ff) {
            return new Promise((resolve) =>
                resolve({
                    case_data: { deliveryETA: new Date().toISOString() },
                    case_id: '',
                    result: 'SUCCESS',
                    error_code: 0,
                }),
            );
        }

        const { type, ...formData } = payload;
        const form = new FormData();
        Object.entries(formData)
            .filter(([, value]) => Boolean(value))
            .forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach((v) => form.append(key, v));
                } else {
                    form.append(key, value);
                }
            });
        const res = await fetch(`${XTRA_UPDATE_REPORTS_DATA_ENDPOINT}/support-request/${type}`, {
            headers: new Headers({
                Authorization: `Token ${userCookieStorage.token}`,
            }),
            method: 'POST',
            body: form,
        });

        return res.json();
    },
    submitRequestFlow(payload: SubmitRequestFlowPayload): Promise<any> {
        return fetchWrapper({
            endpoint: '',
            basePath: '',
            targetUrl: `${XTRA_UPDATE_REPORTS_DATA_ENDPOINT}/submit-support-request`,
            method: 'POST',
            body: payload,
        });
    },
    getOfflineReportsSharedWithMe(): Promise<{ requests: OrderManagementRequest[] }> {
        const params = {
            targetUrl: `${XTRA_GET_REPORTS_DATA_ENDPOINT}/get-all-cases/?owner=shared`,
            endpoint: '',
            basePath: '',
        };
        return fetchWrapper({
            ...params,
        });
    },
    getOfflineReportsAccount(): Promise<{ requests: OrderManagementRequest[] }> {
        const xtraReportsGetUrl = `${XTRA_GET_REPORTS_DATA_ENDPOINT}/get-all-account-cases/`;

        return fetchWrapper({
            targetUrl: xtraReportsGetUrl,
            endpoint: '',
            basePath: '',
        });
    },
    getOfflineReportsUserAccountInfo: () => {
        return fetchWrapper<UserAccountDetails>({
            targetUrl: '/user/account/',
        });
    },
    getOfflineReportsConfigurations(): Promise<OfflineReportsConfiguration> {
        return fetchWrapper({ targetUrl: '/offline_reports/configurations/' });
    },
    getOfflineReportsRequests(): Promise<{
        requests: OrderManagementRequest[];
    }> {
        const params = {
            targetUrl: `${XTRA_GET_REPORTS_DATA_ENDPOINT}/get-all-cases/`,
            endpoint: '',
            basePath: '',
        };
        return fetchWrapper({
            ...params,
        });
    },
    getOfflineReportsRequestsComments(requestId: string): Promise<{ comments: Array<Comment> }> {
        const params = {
            targetUrl: `${XTRA_GET_REPORTS_DATA_ENDPOINT}/${requestId}/comments`,
            endpoint: '',
            basePath: '',
        };
        return fetchWrapper({
            ...params,
        });
    },
    postOfflineReportsComment(
        requestId: string,
        payload: NewCommentPayload,
    ): Promise<{ result: 'SUCCESS'; comments: Array<any> }> {
        const params = {
            targetUrl: `${XTRA_UPDATE_REPORTS_DATA_ENDPOINT}/${requestId}/addComment`,
            endpoint: '',
            basePath: '',
        };
        return fetchWrapper({
            ...params,
            method: 'POST',
            body: payload,
        });
    },
    shareOfflineReportsComment(
        requestId: string,
        payload: ShareReportPayload,
    ): Promise<PlacerResponseData<{}>> {
        return fetchWrapper({
            targetUrl: `/offline_reports/orders/${requestId}/share/`,
            method: 'POST',
            body: payload,
        });
    },
    getCreditsBalance(): Promise<OfflineReportsCreditsBalance> {
        return fetchWrapper({ targetUrl: '/user/credit_balance' });
    },
    updateReadReceipt(requestId: string, flag: boolean): Promise<{ result: 'SUCCESS' }> {
        const params = {
            targetUrl: `${XTRA_UPDATE_REPORTS_DATA_ENDPOINT}/${requestId}/read`,
            endpoint: '',
            basePath: '',
        };
        return fetchWrapper({
            ...params,
            method: 'POST',
            body: { flag },
        });
    },
    getRequestMaterialZip(
        id: number,
        filesList: AttachmentItem[],
        contentTypeHeader?: string,
    ): Promise<Response> {
        const params = {
            passedURL: `${XTRA_GET_REPORTS_DATA_ENDPOINT}/${id}/download`,
            baseUrl: '',
            basePath: '',
        };
        return fetchWithBasePath({
            ...params,
            options: {
                method: 'POST',
                body: JSON.stringify({
                    filesList,
                    contentTypeHeader,
                }),
            },
        });
    },
    getTableauEmbeddedJWTtoken(): Promise<TableauTicketResponse> {
        const reactAppGateway = getAppGatewayEndpoint();
        const xtraReportsUpdateUrl = `${reactAppGateway}/tableau-jwt-service/getJwtToken/token`;

        const params = {
            targetUrl: xtraReportsUpdateUrl,
            basePath: '',
            endpoint: '',
        };

        return fetchWrapper({
            ...params,
        });
    },
    getSupportRequests(): Promise<{
        requests: SupportRequest[];
    }> {
        const supportTeams = "'Chains', 'Core Support', 'Data Quality', 'GIS'";

        const params = {
            targetUrl: `${XTRA_GET_REPORTS_DATA_ENDPOINT}/get-all-support-cases/?support_teams=${supportTeams}`,
            endpoint: '',
            basePath: '',
        };
        return fetchWrapper({
            ...params,
        });
    },
    async postEmailMsg({
        caseId,
        comment,
        files,
        requestSubject,
    }: EmailMessagePayloadWithAttachments): Promise<{ result: 'SUCCESS'; comments: Array<any> }> {
        const form = new FormData();
        form.append('comment', comment);
        files.forEach((file) => {
            form.append('files', file);
        });

        requestSubject && form.append('requestSubject', requestSubject);

        const headers = new Headers();
        headers.append('Authorization', `Token ${userCookieStorage.token}`);

        const postEmailMsgResponse = await fetch(
            `${XTRA_UPDATE_REPORTS_DATA_ENDPOINT}/${caseId}/addCommentWithAttachment`,
            {
                method: 'POST',
                headers: headers,
                body: form,
            },
        );
        return await postEmailMsgResponse.json();
    },
};
